import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BookText, Map, ScrollText, ArrowRight, Volume2, Sparkles, Clock, Target, School, GraduationCap, UserRound, Share2 } from "lucide-react";
import { getWordOfTheDay, getStats, getProverbOfTheDay } from "@/lib/api";
import { toast } from "sonner";
import { IMAGES } from "@/lib/images";
import { Ornament } from "@/components/Ornament";

const features = [
  { to: "/sozluk", icon: BookText, title: "Aranabilir Sözlük", desc: "Eski Türkçe kelimeleri kökeni, Kaşgarlı alıntısı ve örnek cümlesiyle keşfedin.", testid: "feature-dictionary" },
  { to: "/harita", icon: Map, title: "Dairevi Dünya Haritası", desc: "Kaşgarlı'nın Balasagun merkezli haritasında Türk boylarını gezin.", testid: "feature-map" },
  { to: "/savlar", icon: ScrollText, title: "Savlar", desc: "11. yüzyıl atasözlerini günümüz Türkçesi karşılıklarıyla okuyun.", testid: "feature-proverbs" },
  { to: "/zaman", icon: Clock, title: "Zaman Yolu", desc: "Dîvân'ın bin yıllık yolculuğunu adım adım izleyin.", testid: "feature-timeline" },
  { to: "/oyun", icon: Target, title: "Kelime Avı", desc: "10 saniyelik sorularla bilginizi sınayın, puan toplayın.", testid: "feature-quiz" },
];

const gallery = [
  { src: IMAGES.steppeHorsemen, label: "Bozkır Süvarileri" },
  { src: IMAGES.yurtCamp, label: "Otağ ve Yurt" },
  { src: IMAGES.silkRoad, label: "İpek Yolu Kervanı" },
  { src: IMAGES.eagleHunter, label: "Kuşçu / Avcı" },
  { src: IMAGES.ozanBard, label: "Ozan ve Kopuz" },
  { src: IMAGES.turkicAlp, label: "Alp Yiğit" },
];

export default function Home() {
  const [wotd, setWotd] = useState(null);
  const [stats, setStats] = useState(null);
  const [proverb, setProverb] = useState(null);

  useEffect(() => {
    getWordOfTheDay().then(setWotd).catch(() => {});
    getStats().then(setStats).catch(() => {});
    getProverbOfTheDay().then(setProverb).catch(() => {});
  }, []);

  const speak = (text) => {
    try {
      const u = new SpeechSynthesisUtterance(text);
      u.lang = "tr-TR";
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(u);
    } catch (e) {}
  };

  const shareWord = async () => {
    if (!wotd) return;
    const text = `📜 Günün Kelimesi — ${wotd.word}\n\n${wotd.meaning}\n\n${wotd.example ? `“${wotd.example}” — ${wotd.example_meaning}\n\n` : ""}Divânu Lugâti't-Türk · Dijital Codex`;
    try {
      if (navigator.share) {
        await navigator.share({ title: `Günün Kelimesi: ${wotd.word}`, text });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success("Kelime kartı panoya kopyalandı");
      }
    } catch (e) {}
  };

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12 grid lg:grid-cols-12 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-6"
          >
            <span className="eyebrow">Kaşgarlı Mahmud · 1074</span>
            <h1 lang="tr" className="mt-4 font-[Cinzel] text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] text-foreground">
              TÜRK DİLİNİN <span className="rubric">İLK HAZİNESİ</span>, DİJİTAL BİR VARAKTA.
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-foreground/80 max-w-xl">
              Dokuz asır önce bozkırdan derlenen kelimeler, savlar ve bir dünya haritası.
              Divânu Lugâti't-Türk'ün ruhunu modern bir okuma deneyimiyle yeniden keşfedin.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/sozluk"
                data-testid="hero-dictionary-btn"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors"
              >
                Sözlüğü Aç <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/harita"
                data-testid="hero-map-btn"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-accent text-foreground hover:bg-accent/10 transition-colors"
              >
                Haritayı Gez
              </Link>
            </div>

            {stats && (
              <div className="mt-10 flex gap-8" data-testid="home-stats">
                {[
                  { n: stats.words, l: "Kelime" },
                  { n: stats.proverbs, l: "Sav" },
                  { n: stats.regions, l: "Boy / Bölge" },
                ].map((s) => (
                  <div key={s.l}>
                    <div className="font-[Cinzel] text-3xl font-bold rubric">{s.n}+</div>
                    <div className="eyebrow text-[0.6rem] mt-1">{s.l}</div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>

          {/* Word of the day folio */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-6"
          >
            {wotd && (
              <div
                data-testid="gunun-kelimesi-card"
                className="folio-frame rounded-lg bg-card p-8 sm:p-10"
              >
                <div className="flex items-center justify-between">
                  <span className="eyebrow flex items-center gap-2">
                    <Sparkles className="w-3.5 h-3.5" /> Günün Kelimesi
                  </span>
                  <span className="text-xs font-mono text-muted-foreground">{wotd.category}</span>
                </div>
                <div className="gold-rule my-5" />
                <div className="flex items-end gap-4 flex-wrap">
                  <h2 className="font-[Cinzel] text-5xl font-bold text-foreground">{wotd.word}</h2>
                  {wotd.gokturk && (
                    <span className="gokturk text-3xl text-accent pb-1">{wotd.gokturk}</span>
                  )}
                  <button
                    onClick={() => speak(wotd.pronunciation)}
                    data-testid="audio-pronounce-btn"
                    aria-label="Telaffuzu dinle"
                    className="mb-2 flex items-center justify-center w-9 h-9 rounded-full border border-accent text-accent hover:bg-accent/10 transition-colors"
                  >
                    <Volume2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={shareWord}
                    data-testid="share-word-btn"
                    aria-label="Kelimeyi paylaş"
                    className="mb-2 flex items-center justify-center w-9 h-9 rounded-full border border-accent text-accent hover:bg-accent/10 transition-colors"
                  >
                    <Share2 className="w-4 h-4" />
                  </button>
                </div>
                <p className="mt-1 text-sm font-mono text-muted-foreground">/{wotd.pronunciation}/</p>
                <p className="mt-5 text-lg leading-relaxed text-foreground/90">{wotd.meaning}</p>
                <blockquote className="mt-5 border-l-2 border-primary pl-4 italic text-foreground/75">
                  {wotd.kasgari_quote}
                </blockquote>
                <div className="mt-6 rounded-md bg-secondary/60 p-4">
                  <p className="gokturk text-base text-foreground">“{wotd.example}”</p>
                  <p className="text-sm text-muted-foreground mt-1">{wotd.example_meaning}</p>
                </div>
                <Link
                  to="/sozluk"
                  className="mt-6 inline-flex items-center gap-2 text-sm rubric font-medium hover:gap-3 transition-all"
                >
                  Tüm sözlüğü gör <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      <div className="flex justify-center text-accent my-4">
        <Ornament className="w-48 h-5" />
      </div>

      {/* Proje Künyesi */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="project-credits">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="folio-frame rounded-lg bg-card px-6 py-9 sm:px-12 text-center relative overflow-hidden"
        >
          <div className="flex justify-center text-accent mb-4">
            <Ornament className="w-36 h-5" />
          </div>
          <span className="eyebrow flex items-center justify-center gap-2">
            <School className="w-3.5 h-3.5" /> Proje Künyesi
          </span>
          <h2 className="mt-4 font-[Cinzel] text-2xl sm:text-3xl font-bold text-foreground leading-snug">
            Zeytinburnu Merkezefendi Anadolu İmam Hatip Lisesi
          </h2>
          <p className="mt-3 text-lg rubric italic">“Dilimizin Zenginlikleri” Projesi</p>

          <div className="gold-rule my-7 max-w-md mx-auto" />

          <p className="eyebrow mb-5">Hazırlayanlar</p>
          <div className="flex flex-col sm:flex-row items-stretch justify-center gap-4 max-w-2xl mx-auto">
            <div className="flex-1 rounded-md border border-border bg-secondary/40 p-5">
              <span className="flex items-center justify-center w-11 h-11 rounded-full border border-accent/50 text-accent mx-auto mb-3">
                <GraduationCap className="w-5 h-5" />
              </span>
              <p className="font-[Cinzel] text-lg font-semibold text-foreground">Selma Çalışkan</p>
              <p className="text-sm text-muted-foreground mt-1">Türk Dili ve Edebiyatı Öğretmeni</p>
            </div>
            <div className="flex-1 rounded-md border border-border bg-secondary/40 p-5">
              <span className="flex items-center justify-center w-11 h-11 rounded-full border border-accent/50 text-accent mx-auto mb-3">
                <UserRound className="w-5 h-5" />
              </span>
              <p className="font-[Cinzel] text-lg font-semibold text-foreground">Metehan Sucu</p>
              <p className="text-sm text-muted-foreground mt-1">11. Sınıf Öğrencisi</p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Codex band */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="folio-frame rounded-lg overflow-hidden"
          >
            <img
              src={IMAGES.heroKasgari}
              alt="Kâşgarlı Mahmud eserini yazarken"
              className="w-full h-72 sm:h-96 object-cover"
              loading="lazy"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="eyebrow">Bir Âlimin Mirası</span>
            <h2 className="mt-3 font-[Cinzel] text-3xl sm:text-4xl font-bold text-foreground">
              Kâşgarlı Mahmud
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-foreground/85 drop-cap">
              11. yüzyılda Türk boylarının dilini, şiirini ve coğrafyasını dolaşarak derleyen
              Kâşgarlı Mahmud, topladığı bu hazineyi Bağdat'ta Divânu Lugâti't-Türk'te
              ölümsüzleştirdi. Bu dijital codex, onun yolculuğunu görseller, bir harita ve
              ezgilerle yeniden canlandırır.
            </p>
            <blockquote className="mt-5 border-l-2 border-primary pl-4 italic text-foreground/75">
              “Türk dilini öğrenmek, zorunlu bir iş değil; akıllıca bir yatırımdır.”
            </blockquote>
          </motion.div>
        </div>
      </section>

      {/* Culture gallery */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="culture-gallery">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <span className="eyebrow">Kültür Atlası</span>
          <h2 className="mt-3 font-[Cinzel] text-3xl sm:text-4xl font-bold text-foreground">
            Bozkırın Hayatı
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {gallery.map((g, i) => (
            <motion.figure
              key={g.label}
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: (i % 3) * 0.08 }}
              className="card-hover group relative rounded-lg overflow-hidden border border-border"
            >
              <img src={g.src} alt={g.label} loading="lazy" className="w-full h-40 sm:h-52 object-cover" />
              <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#1F1510]/85 to-transparent p-3">
                <span className="font-[Cinzel] text-sm text-[#F5EFEB]">{g.label}</span>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="eyebrow">Üç Kapı</span>
          <h2 className="mt-3 font-[Cinzel] text-3xl sm:text-4xl font-bold text-foreground">
            Eseri keşfetmenin yolları
          </h2>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {features.map((f, i) => (
            <motion.div
              key={f.to}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
            >
              <Link
                to={f.to}
                data-testid={f.testid}
                className="card-hover group block h-full rounded-lg border border-border bg-card p-7"
              >
                <span className="flex items-center justify-center w-12 h-12 rounded-full border border-accent/50 text-accent mb-5">
                  <f.icon className="w-5 h-5" />
                </span>
                <h3 className="font-[Cinzel] text-xl font-semibold text-foreground">{f.title}</h3>
                <p className="mt-3 text-foreground/75 leading-relaxed">{f.desc}</p>
                <span className="mt-5 inline-flex items-center gap-2 text-sm rubric font-medium">
                  Keşfet <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Featured proverb */}
      {proverb && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="folio-frame rounded-lg bg-card px-6 py-12 sm:px-16 text-center" data-testid="home-proverb">
            <span className="eyebrow">Günün Savı</span>
            <p className="mt-5 font-[Cinzel] text-2xl sm:text-3xl leading-snug text-foreground">
              “{proverb.original}”
            </p>
            <p className="mt-4 text-lg rubric italic">{proverb.modern}</p>
            {proverb.explanation && <p className="mt-3 max-w-xl mx-auto text-foreground/70">{proverb.explanation}</p>}
            <Link
              to="/savlar"
              className="mt-7 inline-flex items-center gap-2 text-sm rubric font-medium hover:gap-3 transition-all"
            >
              Tüm savları oku <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}
